import {cn} from '@tempmee/core'
import type React from 'react'
import {type ReactNode, forwardRef} from 'react'
import {Tooltip} from '../tooltip'
import {MenuItemVariantsFactory} from './Variants'
import {useSidebarCollapsed} from './collapsed'
import {useMenuColor} from './color'
import {useMenuDensity} from './density'
import {useMenuSize} from './size'

interface MenuItemBaseProps {
  /** Leading icon (24×24). */
  icon?: ReactNode
  /**
   * Trailing icon pinned to the row's end — the Figma `MenuItem` `Expand` slot
   * (a chevron, or an external-link icon on an off-site row). Keeps its own
   * colour: pass it on the icon (e.g. `className="text-primary"`). Hidden in a
   * collapsed rail, like the label.
   */
  trailingIcon?: ReactNode
  /** Whether this item is selected (filled teal pill). */
  selected?: boolean
  /** Whether this item is disabled. */
  disabled?: boolean
  /** Compact 32px / 14px row. Inherits from `Sidebar dense` /
   * `MenuDensityProvider` when omitted, else `false`. */
  dense?: boolean
  /** Taller 48px touch-target row (16px text) for small screens. Overrides `dense`. @default false */
  smallScreen?: boolean
  className?: string
  children: ReactNode
}

/** Link form — renders `<a>` when `href` is provided. */
type MenuItemLinkProps = MenuItemBaseProps &
  Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, keyof MenuItemBaseProps> & {href: string}

/** Button form — renders `<button>` when `href` is absent. */
type MenuItemButtonProps = MenuItemBaseProps &
  Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, keyof MenuItemBaseProps> & {href?: never}

export type MenuItemProps = MenuItemLinkProps | MenuItemButtonProps

export const MenuItem = forwardRef<HTMLElement, MenuItemProps>(
  (
    {
      icon,
      trailingIcon,
      selected = false,
      disabled = false,
      dense: denseProp,
      smallScreen = false,
      href,
      className,
      children,
      ...props
    },
    ref,
  ) => {
    // Explicit `dense` wins; otherwise inherit from `Sidebar dense` / provider.
    const inheritedDense = useMenuDensity()
    const dense = denseProp ?? inheritedDense
    // Collapsed (mini) rail → icon only, centered, with the label as a tooltip.
    const collapsed = useSidebarCollapsed()
    // Surface tone — `dark` (the brand rail) renders white rows.
    const color = useMenuColor()
    // Inherited row height (`lg` = the 48px nav rail); `dense`/`smallScreen` win.
    const size = useMenuSize()
    const classes = cn(
      MenuItemVariantsFactory({color, size, selected, disabled, dense, smallScreen}),
      collapsed && 'justify-center',
      className,
    )

    const iconEl = icon && (
      <span className="flex size-6 shrink-0 items-center justify-center">{icon}</span>
    )
    const content = collapsed ? (
      (iconEl ?? <span className="truncate text-left">{children}</span>)
    ) : (
      <>
        {iconEl}
        <span className="min-w-0 flex-1 truncate text-left">{children}</span>
        {/* Truthy, like `icon`: `trailingIcon={cond && <Icon />}` passes `false`. */}
        {trailingIcon ? (
          <span className="flex shrink-0 items-center gap-2">{trailingIcon}</span>
        ) : null}
      </>
    )

    // Shared across the link/button forms so their per-tag branch stays tiny.
    // In the mini rail the hidden label is exposed as the accessible name.
    const shared = {
      role: 'menuitem' as const,
      'aria-current': selected ? ('page' as const) : undefined,
      'aria-label': collapsed && typeof children === 'string' ? children : undefined,
      className: classes,
    }

    const element = href ? (
      <a
        ref={ref as React.Ref<HTMLAnchorElement>}
        href={disabled ? undefined : href}
        aria-disabled={disabled || undefined}
        {...shared}
        {...(props as React.AnchorHTMLAttributes<HTMLAnchorElement>)}
      >
        {content}
      </a>
    ) : (
      <button
        ref={ref as React.Ref<HTMLButtonElement>}
        type="button"
        disabled={disabled}
        {...shared}
        {...(props as React.ButtonHTMLAttributes<HTMLButtonElement>)}
      >
        {content}
      </button>
    )

    // In the mini rail, the hidden label becomes a right-anchored tooltip.
    return collapsed ? (
      <Tooltip title={children} placement="right">
        {element}
      </Tooltip>
    ) : (
      element
    )
  },
)

MenuItem.displayName = 'MenuItem'
